import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, ShieldAlert, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { usePilotContext } from '../hooks/usePilotContext';
import { ReportAssistPanel } from '../components/ReportAssistPanel';

export function ScenariosPage() {
  const { context } = usePilotContext();
  const [summary, setSummary] = useState(null);
  const [assets, setAssets] = useState([]);
  const [suppressions, setSuppressions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [applySuppressions, setApplySuppressions] = useState(false);

  const [explainOpen, setExplainOpen] = useState(false);
  const [explainIp, setExplainIp] = useState('');
  const [explainBody, setExplainBody] = useState(null);
  const [explainLoading, setExplainLoading] = useState(false);

  const [suppressOpen, setSuppressOpen] = useState(false);
  const [suppressFp, setSuppressFp] = useState('');
  const [suppressReason, setSuppressReason] = useState('Pilot: accepted risk / false positive');
  const [suppressSaving, setSuppressSaving] = useState(false);

  const loadRisk = useCallback(async () => {
    if (!context.customer_id) return;
    setLoading(true);
    try {
      const sumQs = applySuppressions ? '?apply_suppressions=true' : '';
      const assetQs = new URLSearchParams({ limit: '25', offset: '0' });
      if (applySuppressions) assetQs.set('apply_suppressions', 'true');
      const [sum, list, sup] = await Promise.all([
        api.get(`/api/risk/${context.customer_id}/summary${sumQs}`),
        api.get(`/api/risk/${context.customer_id}/assets?${assetQs}`),
        api.get(`/api/findings/${context.customer_id}/suppressions`).catch(() => ({ items: [] })),
      ]);
      setSummary(sum);
      setAssets(list.assets || []);
      setSuppressions(sup.items || []);
    } catch (e) {
      console.error('Risk load failed:', e);
      toast.error(e.message || 'Failed to load risk data');
    } finally {
      setLoading(false);
    }
  }, [context.customer_id, applySuppressions]);

  useEffect(() => {
    loadRisk();
  }, [loadRisk]);

  const runScan = async () => {
    setScanning(true);
    try {
      await api.post(`/api/risk/${context.customer_id}/scan`, {});
      await loadRisk();
      toast.success('Risk scan complete');
    } catch (e) {
      console.error('Risk scan failed:', e);
      toast.error(e.message || 'Risk scan failed');
    } finally {
      setScanning(false);
    }
  };

  const openExplain = async (ip) => {
    setExplainIp(ip);
    setExplainBody(null);
    setExplainOpen(true);
    setExplainLoading(true);
    try {
      const data = await api.get(
        `/api/risk/${context.customer_id}/explain?ip=${encodeURIComponent(ip)}`,
      );
      setExplainBody(data);
    } catch (e) {
      toast.error(e.message || 'Explain failed');
      setExplainOpen(false);
    } finally {
      setExplainLoading(false);
    }
  };

  const openSuppress = (fingerprint) => {
    setSuppressFp(fingerprint);
    setSuppressReason('Pilot: accepted risk / false positive');
    setSuppressOpen(true);
  };

  const saveSuppress = async () => {
    if (!suppressFp.trim()) return;
    setSuppressSaving(true);
    try {
      await api.post(`/api/findings/${context.customer_id}/suppressions`, {
        fingerprint: suppressFp.trim(),
        reason: suppressReason || undefined,
      });
      toast.success('Suppression saved — refresh lists with suppressions applied.');
      setSuppressOpen(false);
      await loadRisk();
    } catch (e) {
      toast.error(e.message || 'Could not save suppression');
    } finally {
      setSuppressSaving(false);
    }
  };

  const topCve = (asset) => {
    const cves = asset.cves || [];
    return cves[0]?.cve_id || null;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Risk</h1>
          <p className="text-muted-foreground">
            Layer 1 asset risk scoring — explain per asset, tune false positives, then drive remediation
            and resimulation.
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-sm">
            <Link className="text-primary underline-offset-4 hover:underline" to="/remediation">
              Remediation
            </Link>
            <span className="text-border">·</span>
            <Link className="text-primary underline-offset-4 hover:underline" to="/resimulation">
              Resimulation
            </Link>
            <span className="text-border">·</span>
            <Link className="text-primary underline-offset-4 hover:underline" to="/verification-loop">
              Verified fix loop
            </Link>
          </div>
        </div>
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-secondary/30 px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={applySuppressions}
              onChange={(e) => setApplySuppressions(e.target.checked)}
            />
            Apply suppressions
          </label>
          <Button onClick={runScan} disabled={scanning}>
            <Play className="mr-2 h-4 w-4" />
            {scanning ? 'Running scan...' : 'Run risk scan'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>
              Active suppressions: <strong className="text-foreground">{suppressions.length}</strong>
            </span>
            {applySuppressions ? (
              <Badge variant="secondary">Summaries reflect suppressions</Badge>
            ) : (
              <span>Toggle &quot;Apply suppressions&quot; to see tuned counts.</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Assets</p>
                <p className="text-2xl font-bold">{summary?.total_assets ?? 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Critical</p>
                <p className="text-2xl font-bold text-red-400">{summary?.critical_count ?? 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">High</p>
                <p className="text-2xl font-bold text-orange-400">{summary?.high_count ?? 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">KEV findings</p>
                <p className="text-2xl font-bold">{summary?.kev_count ?? 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Avg risk</p>
                <p className="text-2xl font-bold">{summary?.avg_risk_score?.toFixed?.(2) ?? '0.00'}</p>
              </CardContent>
            </Card>
          </div>

          {!loading && <ReportAssistPanel hasReport={!!summary?.has_report} />}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldAlert className="h-4 w-4" />
                Top assets
              </CardTitle>
              <CardDescription>
                Sorted by priority. Use Explain for score factors and attack paths; suppress per-CVE when
                tuned.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {assets.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No risk assets yet — ingest discovery and run a risk scan.
                  </p>
                )}
                {assets.map((asset) => {
                  const cve = topCve(asset);
                  const fp = cve ? `cve:${cve}:${asset.ip}` : '';
                  return (
                    <div
                      key={`${asset.ip}-${asset.hostname}`}
                      className="flex flex-col gap-3 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <p className="font-medium">{asset.hostname || asset.ip}</p>
                        <p className="text-xs text-muted-foreground">
                          {asset.device_type || 'unknown'} | {asset.ip}
                          {cve ? (
                            <span className="ml-2 font-mono text-[11px] text-primary">{cve}</span>
                          ) : null}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline">{asset.breach_likelihood || 'unknown'}</Badge>
                        <Badge variant="secondary">{(asset.risk_score ?? 0).toFixed(2)}</Badge>
                        <Button type="button" variant="outline" size="sm" onClick={() => openExplain(asset.ip)}>
                          <Sparkles className="mr-1 h-3.5 w-3.5" />
                          Explain
                        </Button>
                        {cve ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => openSuppress(fp)}
                          >
                            Suppress CVE
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={explainOpen} onOpenChange={setExplainOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Explain risk
              {explainIp ? (
                <span className="font-mono text-sm font-normal text-muted-foreground">{explainIp}</span>
              ) : null}
            </DialogTitle>
            <DialogDescription>
              Deterministic factors and registry attack paths for this asset (not a black-box score).
            </DialogDescription>
          </DialogHeader>
          {explainLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : explainBody ? (
            <div className="space-y-4 text-sm">
              {explainBody.explainability?.score_factors ? (
                <div>
                  <p className="mb-2 font-medium text-foreground">Score factors (0–1)</p>
                  <ul className="grid grid-cols-2 gap-2 font-mono text-xs text-muted-foreground">
                    {Object.entries(explainBody.explainability.score_factors).map(([k, v]) => (
                      <li key={k}>
                        {k}: <span className="text-foreground">{typeof v === 'number' ? v.toFixed(3) : v}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {explainBody.attack_paths_from_registry?.length ? (
                <div>
                  <p className="mb-2 font-medium text-foreground">Attack paths (registry)</p>
                  <ul className="space-y-2">
                    {explainBody.attack_paths_from_registry.map((p) => (
                      <li
                        key={p.path_id}
                        className="rounded-md border border-border bg-secondary/20 p-2 text-xs"
                      >
                        <span className="font-mono text-primary">{p.path_id}</span> — {p.attack_name} (
                        {p.domain}) MITRE: {(p.mitre_ids || []).join(', ') || '—'}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No matching registry paths for this target.</p>
              )}
              <details className="rounded-md border border-border bg-secondary/10 p-2">
                <summary className="cursor-pointer text-xs font-medium">Raw JSON</summary>
                <pre className="mt-2 max-h-48 overflow-auto text-[10px] leading-relaxed">
                  {JSON.stringify(explainBody, null, 2)}
                </pre>
              </details>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={suppressOpen} onOpenChange={setSuppressOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suppress finding</DialogTitle>
            <DialogDescription>
              Fingerprints use <code className="text-xs">cve:CVE-ID:ip</code> (asset-scoped) or{' '}
              <code className="text-xs">cve:CVE-ID</code> (global for tenant). Applies to summaries when
              &quot;Apply suppressions&quot; is on.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Fingerprint</p>
              <Input value={suppressFp} onChange={(e) => setSuppressFp(e.target.value)} className="font-mono text-xs" />
            </div>
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Reason</p>
              <Input value={suppressReason} onChange={(e) => setSuppressReason(e.target.value)} />
            </div>
            <Button type="button" onClick={saveSuppress} disabled={suppressSaving || !suppressFp.trim()}>
              {suppressSaving ? 'Saving…' : 'Save suppression'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
